import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

// ── Searchable dropdown ───────────────────────────────────────────────────────
function SearchDropdown({ options, value, onChange, placeholder, getLabel, getMeta }) {
  const [search, setSearch] = useState('');
  const [open,   setOpen]   = useState(false);

  const selected = value != null ? options.find(o => o.id === value) : null;

  const filtered = options.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return getLabel(o).toLowerCase().includes(q) ||
      (getMeta ? getMeta(o).toLowerCase().includes(q) : false);
  });

  if (selected && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', border: `1.5px solid ${C.primary}`, borderRadius: '8px', background: C.primaryLight }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: C.primaryDeep, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getLabel(selected)}
          </div>
          {getMeta && (
            <div style={{ fontSize: '11px', color: C.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getMeta(selected)}
            </div>
          )}
        </div>
        <button type="button" onClick={() => { setSearch(''); setOpen(true); }}
          style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '5px', background: `${C.primary}20`, color: C.primaryDeep, border: `1px solid ${C.primary}40`, cursor: 'pointer', fontWeight: 600 }}>
          Change
        </button>
        <button type="button" onClick={() => onChange(null)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSub, fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        autoFocus={open}
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder={placeholder || 'Search…'}
        style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{ marginTop: '4px', border: `1.5px solid ${C.border}`, borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', background: C.white, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '14px', textAlign: 'center', color: C.textSub, fontSize: '12px' }}>
              {options.length === 0 ? 'No options available' : 'No results found'}
            </div>
          ) : filtered.slice(0, 60).map(o => (
            <div key={o.id}
              onMouseDown={() => { onChange(o.id); setSearch(''); setOpen(false); }}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: value === o.id ? C.primaryLight : 'transparent' }}
              onMouseEnter={e => { if (value !== o.id) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { if (value !== o.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{getLabel(o)}</div>
              {getMeta && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{getMeta(o)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  open:          { label: 'Open',        bg: '#DBEAFE', color: '#1E40AF', dot: '#3B82F6' },
  'in-progress': { label: 'In Progress', bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
  billed:        { label: 'Billed',      bg: '#FEF3C7', color: '#B45309', dot: '#F59E0B' },
  resolved:      { label: 'Resolved',    bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
  completed:     { label: 'Completed',   bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
  closed:        { label: 'Closed',      bg: '#F3F4F6', color: '#374151', dot: '#9CA3AF' },
};
const STATUSES = ['open', 'in-progress', 'resolved', 'closed'];
const emptyForm = { client_id: null, installer_id: null, reason: '', status: 'open', notes: '' };

export default function Service() {
  const [rows,       setRows]       = useState([]);
  const [clients,    setClients]    = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [stFilter,   setStFilter]   = useState('');
  const [modal,      setModal]      = useState(null);  // null | 'add' | row-object
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');
  const [toast,      setToast]      = useState(null);
  const [delRow,     setDelRow]     = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  // Complete (paid / non-paid)
  const [doneRow,  setDoneRow]  = useState(null);
  const [billable, setBillable] = useState(false);
  const [cost,     setCost]     = useState('');
  const [doneBusy, setDoneBusy] = useState(false);
  const [doneErr,  setDoneErr]  = useState('');

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [svcR, cliR, usrR] = await Promise.all([
        api.get('/api/services'),
        api.get('/api/clients'),
        api.get('/api/users'),
      ]);
      setRows(svcR.data);
      // Only admin-managed clients (no dealer_id)
      setClients(cliR.data.filter(c => !c.dealer_id));
      // Only users with role 'installer'
      setInstallers(usrR.data.filter(u => u.role === 'installer'));
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm); setErr(''); setModal('add'); };
  const openEdit = (row) => {
    setForm({
      client_id:    row.client_id,
      installer_id: row.installer_id,
      reason:       row.reason,
      status:       row.status,
      notes:        row.notes || '',
    });
    setErr('');
    setModal(row);
  };

  const save = async () => {
    if (!form.client_id)         return setErr('Select a client.');
    if (!form.installer_id)      return setErr('Select an installer.');
    if (!form.reason?.trim())    return setErr('Reason is required.');
    setSaving(true); setErr('');
    try {
      const payload = {
        client_id:    form.client_id,
        installer_id: form.installer_id,
        reason:       form.reason.trim(),
        status:       form.status,
        notes:        form.notes?.trim() || null,
      };
      if (modal === 'add') {
        await api.post('/api/services', payload);
        showToast('success', 'Service request created.');
      } else {
        await api.put(`/api/services/${modal.id}`, payload);
        showToast('success', 'Service request updated.');
      }
      setModal(null);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const openComplete = (row) => { setDoneRow(row); setBillable(false); setCost(''); setDoneErr(''); };
  const confirmComplete = async () => {
    if (billable && !(Number(cost) > 0)) { setDoneErr('Enter the service cost.'); return; }
    setDoneBusy(true); setDoneErr('');
    try {
      const r = await api.post(`/api/services/${doneRow.id}/complete`, {
        billable, cost: billable ? Number(cost) : null,
      });
      setDoneRow(null);
      load();
      if (billable) showToast('success', `Charge ₹${Number(cost).toLocaleString('en-IN')} added to Credit List — collect to complete.`);
      else          showToast('success', `${r.data.service_id} completed (no charge).`);
    } catch (e) {
      setDoneErr(e?.response?.data?.detail || 'Could not complete.');
    } finally { setDoneBusy(false); }
  };

  const confirmDelete = async () => {
    if (!delRow) return;
    setDeleting(true);
    try {
      await api.delete(`/api/services/${delRow.id}`);
      showToast('success', `${delRow.service_id} deleted.`);
      setDelRow(null);
      load();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setDeleting(false); }
  };

  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    return (
      (!stFilter || r.status === stFilter) &&
      (!q || r.service_id?.toLowerCase().includes(q)
          || r.client_name?.toLowerCase().includes(q)
          || r.installer_name?.toLowerCase().includes(q)
          || r.reason?.toLowerCase().includes(q))
    );
  });

  const counts = {
    total:      rows.length,
    open:       rows.filter(r => r.status === 'open').length,
    inProgress: rows.filter(r => r.status === 'in-progress').length,
    resolved:   rows.filter(r => r.status === 'resolved').length,
  };

  return (
    <Layout title="Service">

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success' ? '#D1FAE5' : '#FEE2E2',
          color: toast.type === 'success' ? '#065F46' : '#991B1B',
          padding: '12px 20px', borderRadius: '10px', fontWeight: 600, fontSize: '14px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {toast.text}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {[
          { label: 'Total',       val: counts.total,      color: C.primary,  bg: C.primaryLight, border: `${C.primary}60` },
          { label: 'Open',        val: counts.open,       color: '#1E40AF',  bg: '#DBEAFE',      border: '#BFDBFE' },
          { label: 'In Progress', val: counts.inProgress, color: '#92400E',  bg: '#FEF3C7',      border: '#FDE68A' },
          { label: 'Resolved',    val: counts.resolved,   color: '#065F46',  bg: '#D1FAE5',      border: '#6EE7B7' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: '12px', padding: '14px 22px', minWidth: '110px' }}>
            <div style={{ fontSize: '24px', fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 600, marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={openAdd} style={btnPrimary}>+ Add Service</button>
      </div>

      {/* Table card */}
      <div style={{ background: C.white, borderRadius: '12px', padding: '20px', border: `1px solid ${C.border}` }}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search ID, client, installer, reason…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selSt}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s]?.label}</option>)}
          </select>
          {(search || stFilter) && (
            <button onClick={() => { setSearch(''); setStFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {rows.length}</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Service ID', 'Client', 'Installer', 'Reason', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={emptyCell}>
                  {rows.length === 0 ? 'No service requests yet. Click "+ Add Service" to create one.' : 'No results match the filters.'}
                </td></tr>
              ) : filtered.map((r, i) => {
                const st = STATUS_CFG[r.status] || STATUS_CFG.open;
                return (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? C.white : '#FDFAFF', borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.primaryDeep }}>{r.service_id}</span>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{r.client_name}</div>
                      <div style={{ fontSize: '11px', color: C.textSub, fontFamily: 'monospace' }}>{r.client_ref}</div>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: '13px', color: C.textMain }}>{r.installer_name}</span>
                    </td>
                    <td style={{ ...td, maxWidth: '260px' }}>
                      <span style={{ fontSize: '13px', color: C.textMain, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {r.reason}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: st.dot, display: 'inline-block', marginRight: '5px', verticalAlign: 'middle' }} />
                        {st.label}
                      </span>
                      {r.cost != null && (
                        <div style={{ fontSize: '10px', marginTop: '4px', fontWeight: 700, color: r.payment_status === 'paid' ? '#065F46' : '#B45309' }}>
                          ₹{r.cost.toLocaleString('en-IN')} · {r.payment_status === 'paid' ? '✓ paid' : 'unpaid'}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: '12px', color: C.textSub }}>{r.created_at}</span>
                      {r.resolved_at && <div style={{ fontSize: '11px', color: '#059669' }}>Done: {r.resolved_at}</div>}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {r.status !== 'completed' && r.status !== 'billed' && (
                          <button onClick={() => openComplete(r)} style={{ padding: '5px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 700, background: '#D1FAE5', color: '#065F46', border: '1px solid #6EE7B7', cursor: 'pointer' }}>✓ Complete</button>
                        )}
                        {r.status === 'billed' && (
                          <span style={{ padding: '5px 10px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, background: '#FEF3C7', color: '#B45309' }}>💳 Awaiting payment</span>
                        )}
                        <button onClick={() => openEdit(r)} style={btnEdit}>Edit</button>
                        <button onClick={() => setDelRow(r)} style={btnDel}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: C.textMain }}>
                {modal === 'add' ? 'New Service Request' : `Edit ${modal.service_id}`}
              </h3>
              <button onClick={() => setModal(null)} style={btnClose}>✕</button>
            </div>

            {/* Client — searchable dropdown */}
            <label style={labelSt}>Client *</label>
            <SearchDropdown
              options={clients}
              value={form.client_id}
              onChange={id => setForm(f => ({ ...f, client_id: id }))}
              placeholder="Search client name or ID…"
              getLabel={c => c.client_name}
              getMeta={c => c.client_id}
            />

            {/* Installer — plain select, installers only */}
            <label style={labelSt}>Installer *</label>
            <select
              value={form.installer_id ?? ''}
              onChange={e => setForm(f => ({ ...f, installer_id: e.target.value ? Number(e.target.value) : null }))}
              style={inputSt}
            >
              <option value="">— Select Installer —</option>
              {installers.map(u => (
                <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
              ))}
            </select>

            {/* Reason */}
            <label style={labelSt}>Reason / Issue *</label>
            <textarea
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              rows={3}
              placeholder="Describe the service reason or issue…"
              style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
            />

            {/* Status */}
            <label style={labelSt}>Status</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputSt}>
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s]?.label}</option>)}
            </select>

            {/* Notes */}
            <label style={labelSt}>Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Any additional notes…"
              style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
            />

            {err && <div style={{ color: '#DC2626', fontSize: '13px', margin: '10px 0 4px' }}>{err}</div>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {delRow && (
        <div style={overlay}>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 700, color: C.textMain }}>Delete Service Request</h3>
            <p style={{ color: C.textSub, fontSize: '14px', marginBottom: '20px' }}>
              Delete <strong>{delRow.service_id}</strong> for <strong>{delRow.client_name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDelRow(null)} style={btnGhost}>Cancel</button>
              <button onClick={confirmDelete} disabled={deleting}
                style={{ ...btnPrimary, background: '#EF4444', borderColor: '#EF4444' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Complete: paid / non-paid ── */}
      {doneRow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '420px' }}>
            <div style={{ padding: '17px 22px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '15px', fontWeight: 800, color: C.textMain }}>✓ Complete Service</div>
              <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{doneRow.service_id} · {doneRow.client_name}</div>
            </div>
            <div style={{ padding: '18px 22px' }}>
              <label style={labelSt}>Is this service chargeable?</label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                {[
                  { v: false, t: '🆓 Non-paid', d: 'Complete now, no charge' },
                  { v: true,  t: '💰 Paid',     d: 'Add a charge to Credit List' },
                ].map(o => (
                  <button key={String(o.v)} onClick={() => { setBillable(o.v); setDoneErr(''); }} style={{
                    flex: 1, padding: '12px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
                    border: `1.5px solid ${billable === o.v ? C.primary : C.border}`,
                    background: billable === o.v ? C.primaryBg : C.white,
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: billable === o.v ? C.primaryDeep : C.textMain }}>{o.t}</div>
                    <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>{o.d}</div>
                  </button>
                ))}
              </div>

              {billable && (
                <div style={{ marginTop: '15px' }}>
                  <label style={labelSt}>Service cost (₹) *</label>
                  <input type="number" min="0" value={cost} autoFocus
                    onChange={e => { setCost(e.target.value); setDoneErr(''); }}
                    style={{ ...inputSt, fontSize: '16px', fontWeight: 700 }} />
                  <div style={{ marginTop: '10px', padding: '10px 13px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '9px', fontSize: '11px', color: '#92400E', lineHeight: 1.5 }}>
                    A ₹{(Number(cost) || 0).toLocaleString('en-IN')} charge for <strong>{doneRow.client_name}</strong> is added to the <strong>Credit List</strong>. The service completes automatically once it's paid.
                  </div>
                </div>
              )}
              {!billable && (
                <div style={{ marginTop: '15px', padding: '10px 13px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '9px', fontSize: '11px', color: '#065F46', lineHeight: 1.5 }}>
                  The service is marked <strong>completed</strong> right away with no charge.
                </div>
              )}
              {doneErr && <div style={{ marginTop: '10px', color: C.danger, fontSize: '12px', fontWeight: 600 }}>{doneErr}</div>}
            </div>
            <div style={{ padding: '13px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDoneRow(null)} disabled={doneBusy} style={btnEdit}>Cancel</button>
              <button onClick={confirmComplete} disabled={doneBusy} style={{ padding: '9px 20px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: doneBusy ? 'not-allowed' : 'pointer', color: '#fff', background: doneBusy ? '#9CA3AF' : billable ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#10B981,#059669)' }}>
                {doneBusy ? 'Working…' : billable ? '💳 Bill & Add to Credit' : '✓ Mark Completed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const th       = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td       = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell= { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selSt    = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = { padding: '9px 20px', background: C.primary, color: C.white, border: `1px solid ${C.primaryDark}`, borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const btnGhost = { padding: '9px 16px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnEdit  = { padding: '6px 14px', background: C.primaryBg, color: C.primaryDeep, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' };
const btnDel   = { padding: '6px 14px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' };
const btnClose = { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub, lineHeight: 1 };
const overlay  = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const modalBox = { background: C.white, borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const labelSt  = { display: 'block', fontSize: '12px', fontWeight: 700, color: C.textSub, marginBottom: '5px', marginTop: '14px', textTransform: 'uppercase', letterSpacing: '0.4px' };
const inputSt  = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: C.white };
